import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/api';
import { RecItem, useStackSlugs } from './RecommendationsCard';
import { FdaDisclaimer, FtcDisclosure } from './Disclosures';

// "Not sure what to take?" (2026-10 restyle). Pick what you'd like to
// improve; we say what's worth it and what isn't. Same honest engine for a
// signed-in person and an anonymous visitor at /quiz.

const GOAL_WORDS = {
  sleep: 'Sleep better', energy: 'More energy', immunity: 'Fewer colds', stress: 'Calmer, steadier mood',
  muscle: 'Build and keep muscle', heart_brain: 'Heart and brain', digestion: 'Gut comfort',
  bone: 'Stronger bones', general_longevity: 'Healthy for longer',
};
const GOAL_ORDER = ['sleep', 'energy', 'stress', 'muscle', 'heart_brain', 'digestion', 'bone', 'immunity', 'general_longevity'];
const COMMON_ALLERGENS = ['fish', 'shellfish', 'soy', 'dairy', 'gluten', 'tree nuts'];
const PREGNANCY_OPTIONS = [
  { value: '', label: 'Not applicable' },
  { value: 'trying', label: 'Trying to conceive' },
  { value: 'pregnant', label: 'Pregnant' },
  { value: 'breastfeeding', label: 'Breastfeeding' },
];

const Pill = ({ on, children, ...props }) => (
  <button type="button" aria-pressed={!!on}
    className={`rounded-full border px-4 py-2 text-base font-medium transition-colors ${on ? 'border-fg-3 bg-elev text-fg' : 'border-line-strong text-fg-2 hover:border-fg-3 hover:text-fg'}`}
    {...props}>{children}</button>
);

const SupplementQuiz = ({
  heading = 'Not sure what to take?',
  subheading = 'Pick what you’d like to improve. We’ll say what’s worth it and what isn’t.',
  showSignupCta = true,
}) => {
  const [step, setStep] = useState('goals'); // goals | safety | results
  const [allGoals, setAllGoals] = useState([]);
  const [goals, setGoals] = useState([]);
  const [pregnancy, setPregnancy] = useState('');
  const [allergens, setAllergens] = useState([]);
  const [medsText, setMedsText] = useState('');
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(false);
  const stack = useStackSlugs();

  useEffect(() => {
    apiClient.get('/api/recommendations/goals')
      .then((res) => setAllGoals(res.data?.goals || []))
      .catch(() => {});
  }, []);

  const ordered = [...allGoals].sort((a, b) => GOAL_ORDER.indexOf(a) - GOAL_ORDER.indexOf(b));
  const toggle = (setter) => (v) => setter((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));

  const submit = async () => {
    setLoading(true); setStep('results');
    try {
      const medications = medsText.split(',').map((m) => m.trim()).filter(Boolean);
      const res = await apiClient.post('/api/recommendations/quiz', { goals, allergies: allergens, pregnancy_status: pregnancy || null, medications });
      setRecs(res.data?.recommendations || []);
    } catch { setRecs([]); } finally { setLoading(false); }
  };

  const hasAffiliate = (recs || []).some((r) => r.affiliate_url);

  return (
    <section className="mt-10">
      <div className="mb-3.5">
        <h2 className="text-[1.2rem] font-medium tracking-[-0.015em]">{heading}</h2>
        <p className="mt-1 text-sm text-fg-3">{subheading}</p>
      </div>

      {step !== 'results' && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {ordered.map((g) => {
            const on = goals.includes(g);
            return (
              <button key={g} type="button" aria-pressed={on} onClick={() => toggle(setGoals)(g)}
                className={`min-h-[84px] rounded-xl border bg-transparent px-4 py-[18px] text-left text-[1.05rem] font-medium leading-[1.25] transition-colors ${
                  on ? 'border-fg-3 bg-raised text-fg' : 'border-line-strong text-fg-2 hover:bg-raised hover:text-fg'}`}>
                {GOAL_WORDS[g] || g}
              </button>
            );
          })}
        </div>
      )}

      {step === 'goals' && goals.length > 0 && (
        <div className="mt-3.5 flex flex-wrap items-center gap-4">
          <button type="button" onClick={() => setStep('safety')} className="rounded-full bg-cta px-[22px] py-3 text-base font-medium text-cta-fg hover:bg-cta-hover">See what’s worth it</button>
          <button type="button" onClick={submit} className="bg-transparent p-0 text-sm font-medium text-fg-3 hover:text-fg">Skip the safety questions</button>
        </div>
      )}

      {step === 'safety' && (
        <div className="mt-3.5 rounded-xl border border-line px-5 py-4">
          <p className="text-base font-medium">Anything we should know first?</p>
          <p className="mt-1 text-sm text-fg-3">All optional. It only keeps the suggestions safe for you. We never suggest changing a medication.</p>
          <p className="mt-4 text-sm text-fg-3">Food allergies</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {COMMON_ALLERGENS.map((a) => <Pill key={a} on={allergens.includes(a)} onClick={() => toggle(setAllergens)(a)}>{a}</Pill>)}
          </div>
          <p className="mt-4 text-sm text-fg-3">Pregnancy</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {PREGNANCY_OPTIONS.map((o) => <Pill key={o.value} on={pregnancy === o.value} onClick={() => setPregnancy(o.value)}>{o.label}</Pill>)}
          </div>
          <label className="mt-4 block text-sm text-fg-3">Medications, separated by commas
            <input type="text" value={medsText} onChange={(e) => setMedsText(e.target.value)} placeholder="warfarin, levothyroxine"
              className="mt-1 flex h-10 w-full rounded-lg border border-line-strong bg-elev px-3 text-base text-fg placeholder:text-fg-3 focus-visible:border-fg-3 focus-visible:outline-none" />
          </label>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <button type="button" onClick={submit} className="rounded-full bg-cta px-[22px] py-3 text-base font-medium text-cta-fg hover:bg-cta-hover">See my suggestions</button>
            <button type="button" onClick={() => setStep('goals')} className="bg-transparent p-0 text-sm font-medium text-fg-3 hover:text-fg">Back</button>
          </div>
        </div>
      )}

      {step === 'results' && (
        <>
          <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3 text-sm text-fg-3">
            <span>For: {goals.map((g) => (GOAL_WORDS[g] || g).toLowerCase()).join(', ')}</span>
            <button type="button" onClick={() => { setRecs(null); setStep('goals'); }} className="bg-transparent p-0 font-medium text-fg-2 hover:text-fg">Start over</button>
          </div>
          {loading ? (
            <div className="h-32 animate-pulse rounded-xl border border-line" />
          ) : (recs || []).length === 0 ? (
            <div className="rounded-xl border border-line px-5 py-5 text-fg-2">Nothing worth suggesting here, and that’s often the honest answer. Food and habits carry most of the weight.</div>
          ) : (
            <div className="divide-y divide-line rounded-xl border border-line">
              {recs.map((r, i) => <RecItem key={`${r.goal}-${r.slug}-${i}`} r={r} stack={stack} />)}
            </div>
          )}
          {showSignupCta && !loading && (
            <div className="mt-3.5 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-line px-5 py-4">
              <div>
                <p className="text-base font-medium">Want to know if it actually works?</p>
                <p className="mt-0.5 text-sm text-fg-3">Add it to your stack and we read what moved after you started.</p>
              </div>
              <Link to="/register" className="rounded-full bg-cta px-[18px] py-2.5 text-base font-medium text-cta-fg hover:bg-cta-hover">Create a free account</Link>
            </div>
          )}
          <div className="mt-3.5 space-y-2">
            <FtcDisclosure hasAffiliate={hasAffiliate} />
            <FdaDisclaimer />
          </div>
        </>
      )}
    </section>
  );
};

export default SupplementQuiz;
